import axios from 'axios';

// Criar instância do axios com configuração base
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
});

// Interceptor para adicionar token JWT automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros de resposta
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('🚨 API Error:', error);

    if (error.response?.status === 401) {
      // Token inválido ou expirado
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    // Retornar erro formatado
    return Promise.reject({
      status: error.response?.status,
      message: error.response?.data?.error || error.message || 'Erro de conexão',
      details: error.response?.data?.details,
      data: error.response?.data
    });
  }
);

// Serviço de autenticação
export const authService = {
  async login(credentials) {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  async register(userData) {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async refreshToken() {
    const response = await api.post('/auth/refresh');
    return response.data;
  }
};

// Serviço de usuários
export const userService = {
  async getUsers(params = {}) {
    const response = await api.get('/users', { params });
    return response.data;
  },

  async getUser(id) {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  async createUser(userData) {
    const response = await api.post('/users', userData);
    return response.data;
  },

  async updateUser(id, userData) {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  async deleteUser(id) {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  async toggleUserStatus(id) {
    const response = await api.patch(`/users/${id}/toggle-status`);
    return response.data;
  },

  async changePassword(id, passwordData) {
    const response = await api.patch(`/users/${id}/password`, passwordData);
    return response.data;
  }
};

// Serviço de grupos de usuários
export const userGroupService = {
  async getUserGroups(params = {}) {
    const response = await api.get('/user-groups', { params });
    return response.data;
  },

  async getUserGroup(id) {
    const response = await api.get(`/user-groups/${id}`);
    return response.data;
  },

  async createUserGroup(groupData) {
    const response = await api.post('/user-groups', groupData);
    return response.data;
  },

  async updateUserGroup(id, groupData) {
    const response = await api.put(`/user-groups/${id}`, groupData);
    return response.data;
  },

  async deleteUserGroup(id) {
    const response = await api.delete(`/user-groups/${id}`);
    return response.data;
  }
};

// Serviço de ativos
export const assetService = {
  async getAssets(params = {}) {
    const response = await api.get('/assets', { params });
    return response.data;
  },

  async getAsset(id, category) {
    // Para ativos físicos, usar endpoint específico
    const endpoint = category === 'physical' ? `/physical-assets/${id}` : `/assets/category/${category}/${id}`;
    const response = await api.get(endpoint);
    return response.data;
  },

  async createAsset(assetData) {
    let endpoint;
    if (assetData.category === 'physical') {
      endpoint = '/physical-assets';
    } else {
      endpoint = `/assets/category/${assetData.category}`;
    }
    const response = await api.post(endpoint, assetData);
    return response.data;
  },

  async updateAsset(id, assetData) {
    let endpoint;
    if (assetData.category === 'physical') {
      endpoint = `/physical-assets/${id}`;
    } else {
      endpoint = `/assets/category/${assetData.category}/${id}`;
    }
    const response = await api.put(endpoint, assetData);
    return response.data;
  },

  async deleteAsset(id, category) {
    // Para ativos físicos, usar endpoint específico
    const endpoint = category === 'physical' ? `/physical-assets/${id}` : `/assets/category/${category}/${id}`;
    const response = await api.delete(endpoint);
    return response.data;
  },

  async getAssetsByCategory(category, subcategory = null) {
    let url;
    if (category === 'physical') {
      url = '/physical-assets';
    } else {
      url = `/assets/category/${category}`;
    }
    const params = {};

    if (subcategory) {
      params.subcategory = subcategory;
    }

    const response = await api.get(url, { params });
    return response.data;
  },

  async getExpiringSoon(days = 30) {
    const response = await api.get('/assets/expiring/soon', {
      params: { days }
    });
    return response.data;
  },

  async getStats() {
    const response = await api.get('/assets/stats/summary');
    return response.data;
  },

  async uploadAssetFiles(files) {
    const formData = new FormData();
    (Array.from(files) || []).forEach((file) => formData.append('files', file));
    const response = await api.post('/assets/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

// Serviço de APIs
export const apiService = {
  async getAPIs(params = {}) {
    const response = await api.get('/apis', { params });
    return response.data;
  },

  async getAPI(id) {
    const response = await api.get(`/apis/${id}`);
    return response.data;
  },

  async createAPI(apiData) {
    const response = await api.post('/apis', apiData);
    return response.data;
  },

  async updateAPI(id, apiData) {
    const response = await api.put(`/apis/${id}`, apiData);
    return response.data;
  },

  async deleteAPI(id) {
    const response = await api.delete(`/apis/${id}`);
    return response.data;
  },

  async getAPIsStats() {
    const response = await api.get('/apis/stats/summary');
    return response.data;
  }
};

// Serviço de Licenças
export const licenseService = {
  async getLicenses(params = {}) {
    const response = await api.get('/licenses', { params });
    return response.data;
  },

  async getLicense(id) {
    const response = await api.get(`/licenses/${id}`);
    return response.data;
  },

  async createLicense(licenseData) {
    const response = await api.post('/licenses', licenseData);
    return response.data;
  },

  async updateLicense(id, licenseData) {
    const response = await api.put(`/licenses/${id}`, licenseData);
    return response.data;
  },

  async deleteLicense(id) {
    const response = await api.delete(`/licenses/${id}`);
    return response.data;
  },

  async getLicensesStats() {
    const response = await api.get('/licenses/stats/summary');
    return response.data;
  }
};

// Serviço de Certificados
export const certificateService = {
  async getCertificates(params = {}) {
    const response = await api.get('/certificates', { params });
    return response.data;
  },

  async getCertificate(id) {
    const response = await api.get(`/certificates/${id}`);
    return response.data;
  },

  async createCertificate(certificateData) {
    const response = await api.post('/certificates', certificateData);
    return response.data;
  },

  async updateCertificate(id, certificateData) {
    const response = await api.put(`/certificates/${id}`, certificateData);
    return response.data;
  },

  async deleteCertificate(id) {
    const response = await api.delete(`/certificates/${id}`);
    return response.data;
  },

  async getCertificatesStats() {
    const response = await api.get('/certificates/stats/summary');
    return response.data;
  }
};

// Serviço de Tipos de Domínio
export const domainTypeService = {
  async getDomainTypes(params = {}) {
    const response = await api.get('/domain-types', { params });
    return response.data;
  },

  async getDomainType(id) {
    const response = await api.get(`/domain-types/${id}`);
    return response.data;
  },

  async createDomainType(domainTypeData) {
    const response = await api.post('/domain-types', domainTypeData);
    return response.data;
  },

  async updateDomainType(id, domainTypeData) {
    const response = await api.put(`/domain-types/${id}`, domainTypeData);
    return response.data;
  },

  async deleteDomainType(id) {
    const response = await api.delete(`/domain-types/${id}`);
    return response.data;
  }
};

// Serviço de Domínios
export const domainService = {
  async getDomains(params = {}) {
    const response = await api.get('/domains', { params });
    return response.data;
  },

  async getDomain(id) {
    const response = await api.get(`/domains/${id}`);
    return response.data;
  },

  async createDomain(domainData) {
    const response = await api.post('/domains', domainData);
    return response.data;
  },

  async updateDomain(id, domainData) {
    const response = await api.put(`/domains/${id}`, domainData);
    return response.data;
  },

  async deleteDomain(id) {
    const response = await api.delete(`/domains/${id}`);
    return response.data;
  },

  async getDomainsStats() {
    const response = await api.get('/domains/stats/summary');
    return response.data;
  }
};

// Serviço de dispositivos homologados
export const homologatedDeviceService = {
  async getHomologatedHierarchy() {
    const response = await api.get('/homologated-devices/hierarchy');
    return response.data;
  },

  async getHomologatedLevels(params = {}) {
    const response = await api.get('/homologated-devices/levels', { params });
    return response.data;
  },

  async getHomologatedLevel(id) {
    const response = await api.get(`/homologated-devices/levels/${id}`);
    return response.data;
  },

  async createHomologatedLevel(levelData) {
    const response = await api.post('/homologated-devices/levels', levelData);
    return response.data;
  },

  async updateHomologatedLevel(id, levelData) {
    const response = await api.put(`/homologated-devices/levels/${id}`, levelData);
    return response.data;
  },

  async deleteHomologatedLevel(id) {
    const response = await api.delete(`/homologated-devices/levels/${id}`);
    return response.data;
  },

  async getHomologatedGroups(params = {}) {
    const response = await api.get('/homologated-devices/groups', { params });
    return response.data;
  },

  async getHomologatedGroup(id) {
    const response = await api.get(`/homologated-devices/groups/${id}`);
    return response.data;
  },

  async createHomologatedGroup(groupData) {
    const response = await api.post('/homologated-devices/groups', groupData);
    return response.data;
  },

  async updateHomologatedGroup(id, groupData) {
    const response = await api.put(`/homologated-devices/groups/${id}`, groupData);
    return response.data;
  },

  async deleteHomologatedGroup(id) {
    const response = await api.delete(`/homologated-devices/groups/${id}`);
    return response.data;
  },

  async getHomologatedDevices(params = {}) {
    const response = await api.get('/homologated-devices', { params });
    return response.data;
  },

  async getHomologatedDevice(id) {
    const response = await api.get(`/homologated-devices/${id}`);
    return response.data;
  },

  async createHomologatedDevice(deviceData) {
    const response = await api.post('/homologated-devices', deviceData);
    return response.data;
  },

  async updateHomologatedDevice(id, deviceData) {
    const response = await api.put(`/homologated-devices/${id}`, deviceData);
    return response.data;
  },

  async deleteHomologatedDevice(id) {
    const response = await api.delete(`/homologated-devices/${id}`);
    return response.data;
  },

  async uploadDeviceImage(imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);
    const response = await api.post('/homologated-devices/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

// Serviço de configuração
export const configService = {
  async getHealth() {
    const response = await api.get('/health');
    return response.data;
  }
};

export const reportService = {
  async getTemplates(params = {}) {
    const response = await api.get('/reports/templates', { params });
    return response.data;
  },

  async runReport(payload) {
    const response = await api.post('/reports/run', payload);
    return response.data;
  },

  async getReports() {
    const response = await api.get('/reports');
    return response.data;
  },

  async createReport(payload) {
    const response = await api.post('/reports', payload);
    return response.data;
  },

  async getReport(id) {
    const response = await api.get(`/reports/${id}`);
    return response.data;
  },

  async updateReport(id, payload) {
    const response = await api.patch(`/reports/${id}`, payload);
    return response.data;
  },

  async deleteReport(id) {
    const response = await api.delete(`/reports/${id}`);
    return response.data;
  },

  async exportReport(id) {
    const response = await api.get(`/reports/${id}/export`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `report_${id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export const alertService = {
  async getRules() {
    const response = await api.get('/alerts/rules');
    return response.data;
  },

  async createRule(payload) {
    const response = await api.post('/alerts/rules', payload);
    return response.data;
  },

  async getRule(id) {
    const response = await api.get(`/alerts/rules/${id}`);
    return response.data;
  },

  async updateRule(id, payload) {
    const response = await api.patch(`/alerts/rules/${id}`, payload);
    return response.data;
  },

  async deleteRule(id) {
    const response = await api.delete(`/alerts/rules/${id}`);
    return response.data;
  },

  async testRule(id) {
    const response = await api.post(`/alerts/rules/${id}/test`);
    return response.data;
  },

  async getHistory(params = {}) {
    const response = await api.get('/alerts/history', { params });
    return response.data;
  },

  async runNow() {
    const response = await api.post('/alerts/run-now');
    return response.data;
  }
};

export const auditService = {
  async getRecentActivities(limit = 10) {
    const response = await api.get('/audit/recent', { params: { limit } });
    return response.data;
  },

  async getAuditLogs(params = {}) {
    const response = await api.get('/audit/logs', { params });
    return response.data;
  }
};

export default api;
